import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { calculateEarnedSalaryPath, uploadEmployeePayslipsPath } from "../../../services/config";

// ── Icons ──────────────────────────────────────────────────────
const ChevronIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 9v4M12 17h.01" />
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
  </svg>
);

// ─── Theme ────────────────────────────────────────────────────────────────────
const buildTheme = (isDark) => ({
  bgPage: isDark ? "#0D0B19" : "#F5F7FA",
  bgCard: isDark ? "#19152C" : "#FFFFFF",
  bgInput: isDark ? "#161421" : "#FFFFFF",
  bgRow: isDark ? "#13101F" : "#FAFAFA",
  textPrimary: isDark ? "#E3E2EB" : "#1A1A2E",
  textMuted: isDark ? "#9998A2" : "#6B6B8A",
  border: isDark ? "#2D2A45" : "#E0E0F0",
  borderFocus: isDark ? "#8660F8" : "#6C63FF",
  primary: isDark ? "#8660F8" : "#6C63FF",
  gradient: isDark ? "linear-gradient(135deg,#8660F8,#7550E8)" : "linear-gradient(135deg,#6C63FF,#5A52D5)",
  success: isDark ? "#22C55E" : "#16A34A",
  successBg: isDark ? "rgba(34,197,94,0.12)" : "rgba(22,163,74,0.08)",
  successBrd: isDark ? "rgba(34,197,94,0.35)" : "rgba(22,163,74,0.35)",
  danger: "#EF4444",
  warning: isDark ? "#F59E0B" : "#D97706",
  warningBg: isDark ? "rgba(245,158,11,0.12)" : "rgba(217,119,6,0.08)",
  cardShadow: isDark ? "0 2px 16px rgba(0,0,0,0.4)" : "0 2px 10px rgba(0,0,0,0.05)",
  cardHoverShadow: isDark ? "0 10px 28px rgba(134,96,248,0.22)" : "0 8px 22px rgba(108,99,255,0.13)",
  primaryShadow: isDark ? "0 4px 14px rgba(134,96,248,0.4)" : "0 4px 12px rgba(108,99,255,0.32)",
});

// ── Constants ─────────────────────────────────────────────────────────────────
const ALL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const NOW = new Date();
const CURR_YEAR = NOW.getFullYear();
const CURR_MONTH = NOW.getMonth();

const getVisibleMonths = (year) => {
  if (Number(year) < CURR_YEAR) return ALL_MONTHS;
  if (Number(year) > CURR_YEAR) return [];
  return ALL_MONTHS.slice(0, CURR_MONTH + 1);
};

const YEARS = Array.from({ length: 5 }, (_, i) => CURR_YEAR - i);
const MONTH_NUM = {
  January: "01", February: "02", March: "03", April: "04",
  May: "05", June: "06", July: "07", August: "08",
  September: "09", October: "10", November: "11", December: "12",
};

const STEPS = [
  { label: "Fetching all employee salary data", subLabel: "Loading salary data for all employees…", icon: "👥" },
  { label: "Generating individual PDFs", subLabel: "Rendering payslips for each employee…", icon: "📄" },
  { label: "Uploading to server", subLabel: "Sending payslip files to backend…", icon: "☁️" },
];

const FY_MONTHS = ["APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB", "MAR"];
const MONTH_TO_IDX = { "04": 0, "05": 1, "06": 2, "07": 3, "08": 4, "09": 5, "10": 6, "11": 7, "12": 8, "01": 9, "02": 10, "03": 11 };

// ── Helper: Check if employee has valid CTC ────────────────────────────────────
const hasValidCTC = (raw) => {
  if (!raw || typeof raw !== "object") return false;
  
  // Essential fields that should exist for a valid employee
  const hasEmployeeId = !!(raw.employeeId || raw.staffId);
  const hasEmployeeName = !!raw.staffName;
  
  // Salary components
  const basicPay = Number(raw.basicPay || 0);
  const netSalary = Number(raw.netSalary || 0);
  
  // Check allowances
  const allowanceTotal = (raw.allowanceList || []).reduce((sum, a) => sum + Number(a.monthlyAmount || 0), 0);
  const newAllowanceTotal = (raw.newAllowanceList || []).reduce((sum, a) => sum + Number(a.monthlyAmount || 0), 0);
  const totalEarnings = basicPay + allowanceTotal + newAllowanceTotal;
  
  // Valid CTC conditions - employee should have at least one positive salary component
  const hasValidBasicPay = basicPay > 0;
  const hasValidNetSalary = netSalary > 0;
  const hasAnyEarnings = totalEarnings > 0;
  
  // Skip if missing basic employee info
  if (!hasEmployeeId || !hasEmployeeName) {
    console.log(`Skipping: Missing employee ID or name`);
    return false;
  }
  
  // Skip if no salary components found
  if (!hasValidBasicPay && !hasValidNetSalary && !hasAnyEarnings) {
    console.log(`Skipping ${raw.staffName}: No CTC configured (Basic: ${basicPay}, Net: ${netSalary}, Earnings: ${totalEarnings})`);
    return false;
  }
  
  return true;
};

// ── PDF STYLES ────────────────────────────────────────────────────────────────
const B = { borderWidth: 0.75, borderColor: "#000", borderStyle: "solid" };
const BR = { borderRightWidth: 0.75, borderRightColor: "#000", borderRightStyle: "solid" };
const BB = { borderBottomWidth: 0.75, borderBottomColor: "#000", borderBottomStyle: "solid" };

const PS = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 8, padding: "20px 26px", backgroundColor: "#fff", color: "#000" },
  title: { fontSize: 12, fontFamily: "Helvetica-Bold", textAlign: "center", textDecoration: "underline", letterSpacing: 1, marginBottom: 2 },
  sub: { fontSize: 8, textAlign: "center", marginBottom: 8, color: "#333" },
  empBox: { ...B, marginBottom: 6 },
  empHdr: { backgroundColor: "#d0d0d0", padding: "3px 5px", fontFamily: "Helvetica-Bold", fontSize: 8, ...BB },
  empRow: { flexDirection: "row", ...BB },
  eL: { flex: 0.9, padding: "2.5px 5px", fontFamily: "Helvetica-Bold", fontSize: 7.5, backgroundColor: "#f0f0f0", ...BR },
  eV: { flex: 1.1, padding: "2.5px 5px", fontSize: 7.5, ...BR },
  eL2: { flex: 0.9, padding: "2.5px 5px", fontFamily: "Helvetica-Bold", fontSize: 7.5, backgroundColor: "#f0f0f0", ...BR },
  eV2: { flex: 1.1, padding: "2.5px 5px", fontSize: 7.5 },
  twoCol: { flexDirection: "row" },
  half: { flex: 1, ...B },
  halfR: { flex: 1, ...B, borderLeftWidth: 0 },
  tHdr: { flexDirection: "row", backgroundColor: "#e8e8e8", ...BB },
  tHdrN: { flex: 1, padding: "3px 4px", fontFamily: "Helvetica-Bold", fontSize: 7.5, ...BR },
  tHdrY: { width: 52, padding: "3px 4px", fontFamily: "Helvetica-Bold", fontSize: 7.5, textAlign: "right", ...BR },
  tHdrA: { width: 52, padding: "3px 4px", fontFamily: "Helvetica-Bold", fontSize: 7.5, textAlign: "right" },
  dRow: { flexDirection: "row", ...BB, minHeight: 13 },
  dN: { flex: 1, padding: "2px 4px", fontSize: 7, ...BR },
  dY: { width: 52, padding: "2px 4px", fontSize: 7, textAlign: "right", ...BR },
  dA: { width: 52, padding: "2px 4px", fontSize: 7, textAlign: "right" },
  gRow: { flexDirection: "row", backgroundColor: "#e0e0e0", ...BB },
  gN: { flex: 1, padding: "2.5px 4px", fontFamily: "Helvetica-Bold", fontSize: 7.5, ...BR },
  gY: { width: 52, padding: "2.5px 4px", fontSize: 7.5, ...BR },
  gA: { width: 52, padding: "2.5px 4px", fontSize: 7.5 },
  sRow: { flexDirection: "row", ...BB },
  sN: { flex: 1, padding: "2.5px 4px", fontSize: 7.5, ...BR },
  sNB: { flex: 1, padding: "2.5px 4px", fontSize: 7.5, fontFamily: "Helvetica-Bold", ...BR },
  sY: { width: 52, padding: "2.5px 4px", fontSize: 7.5, ...BR },
  sA: { width: 52, padding: "2.5px 4px", fontSize: 7.5, textAlign: "right" },
  sAB: { width: 52, padding: "2.5px 4px", fontSize: 7.5, textAlign: "right", fontFamily: "Helvetica-Bold" },
  inW: { fontSize: 7.5, marginTop: 4, marginBottom: 5 },
  bot: { flexDirection: "row", marginTop: 3 },
  botL: { flex: 1, marginRight: 4 },
  botR: { flex: 1.7 },
  taxHdr: { ...B, padding: "3px 5px", fontFamily: "Helvetica-Bold", fontSize: 8, backgroundColor: "#d0d0d0", ...BB },
  taxRow: { flexDirection: "row", borderLeftWidth: 0.75, borderLeftColor: "#000", borderLeftStyle: "solid", borderRightWidth: 0.75, borderRightColor: "#000", borderRightStyle: "solid", ...BB, minHeight: 15 },
  tC1: { flex: 1, padding: "2px 4px", fontSize: 7, ...BR },
  tC2: { flex: 1, padding: "2px 4px", fontSize: 7 },
  slRow: { flexDirection: "row", alignItems: "center", marginTop: 5, marginBottom: 3 },
  slL: { fontSize: 7.5, width: 90 },
  slD: { flex: 1, borderBottomWidth: 0.75, borderBottomColor: "#000", borderBottomStyle: "solid" },
  slV: { fontSize: 7.5, width: 55, textAlign: "right" },
  tdsHdr: { ...B, padding: "3px 5px", fontFamily: "Helvetica-Bold", fontSize: 8, backgroundColor: "#d0d0d0", ...BB },
  tdsChdr: { flexDirection: "row", borderLeftWidth: 0.75, borderLeftColor: "#000", borderLeftStyle: "solid", borderRightWidth: 0.75, borderRightColor: "#000", borderRightStyle: "solid", ...BB, backgroundColor: "#e8e8e8" },
  tdsCN: { flex: 1.6, padding: "2.5px 3px", fontFamily: "Helvetica-Bold", fontSize: 7, ...BR },
  tdsCG: { flex: 1, padding: "2.5px 3px", fontFamily: "Helvetica-Bold", fontSize: 7, textAlign: "right", ...BR },
  tdsCA: { flex: 1, padding: "2.5px 3px", fontFamily: "Helvetica-Bold", fontSize: 7, textAlign: "right" },
  tdsRow: { flexDirection: "row", borderLeftWidth: 0.75, borderLeftColor: "#000", borderLeftStyle: "solid", borderRightWidth: 0.75, borderRightColor: "#000", borderRightStyle: "solid", ...BB, minHeight: 13 },
  tdsRN: { flex: 1.6, padding: "2px 3px", fontSize: 7, ...BR },
  tdsRG: { flex: 1, padding: "2px 3px", fontSize: 7, textAlign: "right", ...BR },
  tdsRA: { flex: 1, padding: "2px 3px", fontSize: 7, textAlign: "right" },
  mGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  mCell: { width: "16.66%", ...B, padding: "2.5px 0", textAlign: "center", fontSize: 7 },
  mCellOn: { width: "16.66%", ...B, padding: "2.5px 0", textAlign: "center", fontSize: 7, backgroundColor: "#555" },
  mCellT: { color: "#fff" },
  footer: { fontSize: 6.5, marginTop: 10, color: "#666", textAlign: "center" },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v) => { const n = parseFloat(v || 0); return isNaN(n) ? "0.00" : n.toFixed(2); };
const oW = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tW = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const numW = (n) => {
  n = Math.floor(Math.abs(n || 0));
  if (!n) return "Zero";
  if (n < 20) return oW[n];
  if (n < 100) return tW[Math.floor(n / 10)] + (n % 10 ? " " + oW[n % 10] : "");
  if (n < 1000) return oW[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + numW(n % 100) : "");
  if (n < 100000) return numW(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + numW(n % 1000) : "");
  if (n < 10000000) return numW(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + numW(n % 100000) : "");
  return numW(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + numW(n % 10000000) : "");
};

const buildFileName = (userId, employeeId, now, monthNum, year) => {
  let safeUserId = String(userId || "").replace(/[^0-9]/g, "");
  if (!safeUserId) safeUserId = "2";

  let staffStr = String(employeeId || "").replace(/[^0-9]/g, "");
  let safeStaffId = "02";

  if (staffStr.length >= 2) {
    safeStaffId = staffStr.slice(-2);
  } else if (staffStr.length === 1) {
    safeStaffId = "0" + staffStr;
  }

  const lastDayOfMonth = new Date(Number(year), Number(monthNum), 0).getDate();
  const day = String(lastDayOfMonth).padStart(2, '0');

  const currentTime = new Date();
  const hour = String(currentTime.getHours()).padStart(2, '0');
  const minute = String(currentTime.getMinutes()).padStart(2, '0');
  const second = String(currentTime.getSeconds()).padStart(2, '0');

  const timestamp = Date.now();
  const randomSuffix = Math.floor(Math.random() * 9000000) + 1000000;

  return `${safeUserId}_${safeStaffId}_${year}_${monthNum}_${day}_${hour}_${minute}_${second}_${timestamp}_${randomSuffix}.pdf`;
};

// ── API Mapper with CTC Validation ────────────────────────────────────────────────
const mapApiEmployee = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  
  // ✅ SKIP employees without valid CTC
  if (!hasValidCTC(raw)) {
    return null;
  }
  
  const earnings = [];
  if (Number(raw.basicPay) > 0)
    earnings.push({ name: "Basic Salary", amount: Number(raw.basicPay), ytd: 0 });
  if (Number(raw.hraPay) > 0)
    earnings.push({ name: "House Rent Allowance", amount: Number(raw.hraPay), ytd: 0 });
  (raw.allowanceList || []).forEach(a => {
    if (a.name && Number(a.monthlyAmount || 0) > 0)
      earnings.push({ name: a.name, amount: Number(a.monthlyAmount), ytd: Number(a.yearlyAmount || 0) });
  });
  (raw.newAllowanceList || []).forEach(a => {
    if (a.name && Number(a.monthlyAmount || 0) > 0)
      earnings.push({ name: a.name, amount: Number(a.monthlyAmount), ytd: Number(a.yearlyAmount || 0) });
  });
  const deductions = [];
  if (Number(raw.totalEmployeePF) > 0) deductions.push({ name: "Provident Fund", amount: Number(raw.totalEmployeePF), ytd: 0 });
  if (Number(raw.totalEmployeeESI) > 0) deductions.push({ name: "ESI", amount: Number(raw.totalEmployeeESI), ytd: 0 });
  if (Number(raw.totalProfessionalTax) > 0) deductions.push({ name: "Professional Tax", amount: Number(raw.totalProfessionalTax), ytd: 0 });
  if (Number(raw.totalEmployeeLWF) > 0) deductions.push({ name: "Labour Welfare Fund", amount: Number(raw.totalEmployeeLWF), ytd: 0 });
  const tdsAmt = Number(raw.salaryCountDays?.tdsAmount || 0);
  if (tdsAmt > 0) deductions.push({ name: "TDS", amount: tdsAmt, ytd: 0 });
  const advAmt = Number(raw.totalAdvancePayAmount || 0);
  if (advAmt > 0) deductions.push({ name: "Advance Recovery", amount: advAmt, ytd: 0 });
  const sd = raw.salaryCountDays || {};
  return {
    employee_id: raw.employeeId || "",
    full_name: raw.staffName || "",
    designation: raw.jobTitle || "",
    department: "",
    pfNumber: "",
    esiNumber: "",
    panNumber: "",
    uanNumber: raw.uanNumber || "",
    bankAccount: raw.accountNumber || "",
    ifscCode: raw.bankIFSC || "",
    netPayDays: String(sd.totalWorkingDays ?? raw.payDaysCount ?? "0"),
    presentDays: String(sd.presentDaysCount ?? "0"),
    doj: raw.dateOfJoin || "",
    branch: "",
    lop: String(sd.absentDaysCount ?? "0"),
    netSalary: Number(raw.netSalary || 0),
    companyName: "",
    earnings,
    deductions,
  };
};

// ── Single Employee Salary Slip PDF ──────────────────────────────────────────
const SalarySlipPDF = ({ emp, monthLabel, companyName, monthNum }) => {
  const activeFyIdx = monthNum ? (MONTH_TO_IDX[monthNum] ?? -1) : -1;
  const BLANK = { name: "", ytd: 0, amount: 0 };
  const MIN_ROWS = 8;
  const totalE = (emp.earnings || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalD = (emp.deductions || []).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const net = Number(emp.netSalary) || (totalE - totalD);
  const eArr = [...(emp.earnings || []), ...Array(Math.max(0, MIN_ROWS - (emp.earnings || []).length)).fill(BLANK)];
  const dArr = [...(emp.deductions || []), ...Array(Math.max(0, MIN_ROWS - (emp.deductions || []).length)).fill(BLANK)];
  while (eArr.length < dArr.length) eArr.push(BLANK);
  while (dArr.length < eArr.length) dArr.push(BLANK);

  return (
    <Document>
      <Page size="A4" style={PS.page}>
        <Text style={PS.title}>SALARY SLIP</Text>
        <Text style={PS.sub}>{(companyName || "Company") + " | " + monthLabel}</Text>
        <View style={PS.empBox}>
          <View style={PS.empHdr}><Text>Employee Details</Text></View>
          {[
            ["Emp ID", emp.employee_id || "", "Employee Name", emp.full_name || ""],
            ["PF No", emp.pfNumber || "", "ESI No", emp.esiNumber || ""],
            ["Net Pay Days", emp.netPayDays || "", "Present Days", emp.presentDays || ""],
            ["DOJ", emp.doj || "", "Designation", emp.designation || ""],
            ["Department", emp.department || "", "Branch", emp.branch || ""],
            ["Bank A/c", emp.bankAccount || "", "IFSC", emp.ifscCode || ""],
            ["PAN", emp.panNumber || "", "Mode of Pay", "Bank Transfer"],
            ["UAN", emp.uanNumber || "", "LOP", String(emp.lop || "0")],
          ].map(([l1, v1, l2, v2], i) => (
            <View key={i} style={PS.empRow}>
              <Text style={PS.eL}>{l1}</Text>
              <Text style={PS.eV}>{v1}</Text>
              <Text style={PS.eL2}>{l2}</Text>
              <Text style={PS.eV2}>{v2}</Text>
            </View>
          ))}
        </View>
        <View style={PS.twoCol}>
          <View style={PS.half}>
            <View style={PS.tHdr}>
              <Text style={PS.tHdrN}>{"Earnings"}</Text>
              <Text style={PS.tHdrY}>{"YTD"}</Text>
              <Text style={PS.tHdrA}>{"Amount"}</Text>
            </View>
            {eArr.map((e, i) => (
              <View key={i} style={PS.dRow}>
                <Text style={PS.dN}>{e.name || ""}</Text>
                <Text style={PS.dY}>{e.name && e.ytd ? fmt(e.ytd) : ""}</Text>
                <Text style={PS.dA}>{e.name && e.amount ? fmt(e.amount) : ""}</Text>
              </View>
            ))}
            <View style={PS.gRow}>
              <Text style={PS.gN}>{"Summary"}</Text>
              <Text style={PS.gY}>{""}</Text>
              <Text style={PS.gA}>{""}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sN}>{"Total Earnings"}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sA}>{fmt(totalE)}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sN}>{"Total Deductions"}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sA}>{fmt(totalD)}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sNB}>{"Net Pay"}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sAB}>{fmt(net)}</Text>
            </View>
          </View>
          <View style={PS.halfR}>
            <View style={PS.tHdr}>
              <Text style={PS.tHdrN}>{"Deductions"}</Text>
              <Text style={PS.tHdrY}>{"YTD"}</Text>
              <Text style={PS.tHdrA}>{"Amount"}</Text>
            </View>
            {dArr.map((d, i) => (
              <View key={i} style={PS.dRow}>
                <Text style={PS.dN}>{d.name || ""}</Text>
                <Text style={PS.dY}>{d.name && d.ytd ? fmt(d.ytd) : ""}</Text>
                <Text style={PS.dA}>{d.name && d.amount ? fmt(d.amount) : ""}</Text>
              </View>
            ))}
            <View style={PS.gRow}>
              <Text style={PS.gN}>{"Deductions"}</Text>
              <Text style={PS.gY}>{""}</Text>
              <Text style={PS.gA}>{""}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sN}>{"Reductions"}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sA}>{""}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sN}>{"Total Deductions"}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sA}>{fmt(totalD)}</Text>
            </View>
            <View style={PS.sRow}>
              <Text style={PS.sN}>{""}</Text>
              <Text style={PS.sY}>{""}</Text>
              <Text style={PS.sA}>{""}</Text>
            </View>
          </View>
        </View>
        <Text style={PS.inW}>{"In Words : " + numW(net) + " Rupees Only"}</Text>
        <View style={PS.bot}>
          <View style={PS.botL}>
            <Text style={PS.taxHdr}>{"New Tax Regime Opted"}</Text>
            {[0, 1, 2, 3].map(i => (
              <View key={i} style={PS.taxRow}>
                <Text style={PS.tC1}>{" "}</Text>
                <Text style={PS.tC2}>{" "}</Text>
              </View>
            ))}
            {[
              ["Total Earnings", fmt(totalE)],
              ["Total Deductions", fmt(totalD)],
              ["Net Pay", fmt(net)],
            ].map(([lbl, val], i) => (
              <View key={i} style={PS.slRow}>
                <Text style={PS.slL}>{lbl}</Text>
                <View style={PS.slD} />
                <Text style={PS.slV}>{val}</Text>
              </View>
            ))}
          </View>
          <View style={PS.botR}>
            <Text style={PS.tdsHdr}>{"TDS Details"}</Text>
            <View style={PS.tdsChdr}>
              <Text style={PS.tdsCN}>{"Description"}</Text>
              <Text style={PS.tdsCG}>{"Gross"}</Text>
              <Text style={PS.tdsCG}>{"Exempt"}</Text>
              <Text style={PS.tdsCA}>{"Taxable"}</Text>
            </View>
            {[0, 1, 2, 3].map(i => (
              <View key={i} style={PS.tdsRow}>
                <Text style={PS.tdsRN}>{" "}</Text>
                <Text style={PS.tdsRG}>{" "}</Text>
                <Text style={PS.tdsRG}>{" "}</Text>
                <Text style={PS.tdsRA}>{" "}</Text>
              </View>
            ))}
            <View style={PS.mGrid}>
              {FY_MONTHS.map((m, i) => (
                <View key={i} style={i === activeFyIdx ? PS.mCellOn : PS.mCell}>
                  <Text style={i === activeFyIdx ? PS.mCellT : {}}>{m}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
        <Text style={PS.footer}>{"This is a computer generated salary slip and does not require a signature."}</Text>
      </Page>
    </Document>
  );
};

// ── Generating Modal ──────────────────────────────────────────────────────────
const GeneratingModal = ({ visible, currentStep, done, error, payslipList, onClose, onViewList, totalEmp, doneEmp, skippedCount, t }) => {
  if (!visible) return null;
  const pct = totalEmp > 0 ? Math.round((doneEmp / totalEmp) * 100) : 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: "20px", padding: "32px 38px", width: "100%", maxWidth: "430px", boxShadow: t.cardShadow }}>
        {done && !error ? (
          <>
            <div style={{ textAlign: "center", marginBottom: "18px" }}>
              <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: t.successBg, border: `2px solid ${t.successBrd}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", color: t.success }}>
                <CheckIcon />
              </div>
            </div>
            <h2 style={{ textAlign: "center", margin: "0 0 4px", fontSize: "16px", fontWeight: "700", color: t.success }}>All Payslips Ready!</h2>
            <p style={{ textAlign: "center", margin: "0 0 4px", fontSize: "12px", color: t.success }}>{totalEmp} payslip{totalEmp !== 1 ? "s" : ""} generated successfully</p>
            {skippedCount > 0 && (
              <div style={{ textAlign: "center", margin: "8px 0", padding: "8px", background: t.warningBg, borderRadius: "8px", border: `1px solid ${t.warning}` }}>
                <p style={{ fontSize: "11px", color: t.warning, margin: 0 }}>
                  ⚠️ {skippedCount} employee(s) skipped due to missing CTC configuration
                </p>
              </div>
            )}
            <p style={{ textAlign: "center", margin: "0 0 20px", fontSize: "11px", color: t.textMuted }}>Each employee has their own individual payslip file.</p>

            <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${t.border}`, borderRadius: "10px", padding: "10px 12px", marginBottom: "14px" }}>
              <p style={{ margin: "0 0 8px", fontSize: "10px", fontWeight: "600", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Generated files ({payslipList.length})
              </p>
              {payslipList.slice(0, 3).map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", borderBottom: i < 2 && i < payslipList.length - 1 ? `1px solid ${t.border}` : "none" }}>
                  <span style={{ color: t.primary, flexShrink: 0 }}><FileIcon /></span>
                  <span style={{ fontSize: "10px", color: t.textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.fileName}</span>
                </div>
              ))}
              {payslipList.length > 3 && (
                <p style={{ margin: "6px 0 0", fontSize: "10px", color: t.textMuted, textAlign: "center" }}>
                  + {payslipList.length - 3} more files…
                </p>
              )}
            </div>

            <button
              onClick={() => { onViewList(); onClose(); }}
              style={{ width: "100%", padding: "11px", borderRadius: "11px", border: "none", background: t.gradient, color: "#fff", fontSize: "13px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "9px" }}
            >
              <EyeIcon /> View All Payslips
            </button>
            <button onClick={onClose} style={{ width: "100%", padding: "9px", borderRadius: "11px", border: `1px solid ${t.border}`, background: "rgba(255,255,255,0.04)", color: t.textMuted, fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              Close
            </button>
          </>
        ) : error ? (
          <>
            <div style={{ textAlign: "center", marginBottom: "18px" }}>
              <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "rgba(239,68,68,0.15)", border: "2px solid rgba(239,68,68,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", fontSize: "26px" }}>❌</div>
            </div>
            <h2 style={{ textAlign: "center", margin: "0 0 6px", fontSize: "16px", fontWeight: "700", color: "#f87171" }}>Generation Failed</h2>
            <p style={{ textAlign: "center", margin: "0 0 20px", fontSize: "12px", color: t.textMuted }}>{error}</p>
            <button onClick={onClose} style={{ width: "100%", padding: "9px", borderRadius: "11px", border: `1px solid ${t.border}`, background: "rgba(255,255,255,0.04)", color: t.textMuted, fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>Close</button>
          </>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div style={{ width: "60px", height: "60px", margin: "0 auto", position: "relative" }}>
                <div style={{ width: "60px", height: "60px", borderRadius: "50%", border: `3px solid ${t.primary}15`, borderTopColor: t.primary, animation: "spin 0.9s linear infinite", position: "absolute" }} />
                <div style={{ width: "44px", height: "44px", borderRadius: "50%", border: `3px solid ${t.primary}10`, borderBottomColor: t.primary, animation: "spin 1.4s linear infinite reverse", position: "absolute", top: "8px", left: "8px" }} />
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>🧾</div>
              </div>
            </div>
            <h2 style={{ textAlign: "center", margin: "0 0 4px", fontSize: "16px", fontWeight: "700", color: t.textPrimary }}>Generating Payslips…</h2>
            {currentStep === 1 && totalEmp > 0 ? (
              <div style={{ textAlign: "center", margin: "0 0 16px" }}>
                <span style={{ fontSize: "12px", color: t.primary }}>{doneEmp} / {totalEmp} employees processed</span>
                <div style={{ height: "3px", borderRadius: "2px", background: "rgba(255,255,255,0.06)", margin: "8px 0 0", overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: "2px", background: t.gradient, width: `${pct}%`, transition: "width 0.3s ease" }} />
                </div>
              </div>
            ) : (
              <p style={{ textAlign: "center", margin: "0 0 16px", fontSize: "12px", color: t.textMuted }}>Please wait, this may take a minute…</p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>
              {STEPS.map((step, i) => {
                const isActive = i === currentStep, isDone = i < currentStep;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "11px", padding: "9px 13px", borderRadius: "9px", background: isActive ? `${t.primary}1A` : isDone ? t.successBg : "rgba(255,255,255,0.03)", border: `1px solid ${isActive ? `${t.primary}40` : isDone ? t.successBrd : t.border}`, transition: "all 0.3s" }}>
                    <div style={{ width: "26px", height: "26px", borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: isActive ? `${t.primary}33` : isDone ? t.successBg : "rgba(255,255,255,0.06)", border: `1.5px solid ${isActive ? t.primary : isDone ? t.success : t.border}` }}>
                      {isDone ? <span style={{ fontSize: "12px", color: t.success }}>✓</span>
                        : isActive ? <div style={{ width: "9px", height: "9px", borderRadius: "50%", border: `2px solid ${t.primary}`, borderTopColor: "transparent", animation: "spin 0.7s linear infinite" }} />
                          : <span style={{ fontSize: "11px", color: t.textMuted, fontWeight: "700" }}>{i + 1}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "12px", fontWeight: "600", color: isActive ? t.primary : isDone ? t.success : t.textMuted }}>{step.label}</div>
                      {isActive && <div style={{ fontSize: "10px", color: t.primary, marginTop: "1px", animation: "pulse 1.5s ease-in-out infinite" }}>{step.subLabel}</div>}
                      {isDone && <div style={{ fontSize: "10px", color: t.textMuted, marginTop: "1px" }}>Completed</div>}
                    </div>
                    <span style={{ fontSize: "14px", flexShrink: 0, opacity: i > currentStep ? 0.3 : 1 }}>{step.icon}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ height: "3px", borderRadius: "2px", background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: "2px", background: t.gradient, width: `${Math.round(((currentStep + 0.5) / STEPS.length) * 100)}%`, transition: "width 0.5s ease" }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Payslip List Modal ─────────────────────────────────────────────────
const PayslipListModal = ({ visible, payslipList, monthLabel, onClose, t }) => {
  const [search, setSearch] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);

  if (!visible) return null;

  const filtered = payslipList.filter(item =>
    !search ||
    item.fileName.toLowerCase().includes(search.toLowerCase()) ||
    item.empName.toLowerCase().includes(search.toLowerCase()) ||
    item.empId.toLowerCase().includes(search.toLowerCase())
  );

  const handleCopy = (url, idx) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const handleView = (item) => {
    window.open(item.blobUrl, "_blank");
  };

  const handleDownload = (item) => {
    const a = document.createElement("a");
    a.href = item.blobUrl;
    a.download = item.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const totalNet = payslipList.reduce((s, item) => s + (item.netSalary || 0), 0);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1001, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)", padding: "20px" }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: "20px", width: "100%", maxWidth: "720px", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>

        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: t.textPrimary }}>
                Individual Payslips
              </h2>
              <p style={{ margin: "3px 0 0", fontSize: "11px", color: t.textMuted }}>
                {monthLabel} · {payslipList.length} employee{payslipList.length !== 1 ? "s" : ""} · Total Net Pay: ₹{fmt(totalNet)}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{ width: "34px", height: "34px", borderRadius: "9px", border: `1px solid ${t.border}`, background: "rgba(255,255,255,0.05)", color: t.textMuted, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            >
              <CloseIcon />
            </button>
          </div>

          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <span style={{ position: "absolute", left: "12px", color: t.textMuted, pointerEvents: "none", display: "flex" }}>
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search by name, employee ID or file name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px 8px 36px", borderRadius: "9px",
                border: `1px solid ${t.border}`, background: t.bgInput,
                color: t.textPrimary, fontSize: "12px", outline: "none", boxSizing: "border-box",
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: "10px", background: "none", border: "none", cursor: "pointer", color: t.textMuted, display: "flex", padding: 0 }}>
                <CloseIcon />
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
            {[
              { label: "Total", val: payslipList.length, color: t.primary, bg: `${t.primary}1A`, brd: `${t.primary}33` },
              { label: "Showing", val: filtered.length, color: t.textMuted, bg: "rgba(255,255,255,0.04)", brd: t.border },
              { label: "Net Pay", val: `₹${fmt(totalNet)}`, color: t.success, bg: t.successBg, brd: t.successBrd },
            ].map(({ label, val, color, bg, brd }) => (
              <div key={label} style={{ flex: 1, padding: "8px 10px", borderRadius: "8px", background: bg, border: `1px solid ${brd}`, textAlign: "center" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color }}>{val}</div>
                <div style={{ fontSize: "9px", color: t.textMuted, marginTop: "1px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 90px 90px 90px", gap: "0", padding: "8px 16px", background: `${t.primary}0D`, borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          {["#", "File name / Employee", "Net Pay", "Preview", "Download"].map((h, i) => (
            <div key={i} style={{ fontSize: "9px", fontWeight: "700", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: i > 2 ? "center" : "left" }}>
              {h}
            </div>
          ))}
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "180px", gap: "10px" }}>
              <span style={{ fontSize: "28px" }}>🔍</span>
              <p style={{ color: t.textMuted, fontSize: "12px", margin: 0 }}>No payslips match "{search}"</p>
            </div>
          ) : (
            filtered.map((item, idx) => (
              <PayslipRow
                key={item.fileName}
                item={item}
                idx={idx}
                total={filtered.length}
                copiedIdx={copiedIdx}
                onView={handleView}
                onDownload={handleDownload}
                onCopy={handleCopy}
                t={t}
              />
            ))
          )}
        </div>

        <div style={{ padding: "12px 24px", borderTop: `1px solid ${t.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "10px", color: t.textMuted }}>
            File format: <code style={{ background: `${t.primary}1A`, padding: "1px 5px", borderRadius: "4px", fontSize: "10px", color: t.primary }}>userId_employeeId_datetime.pdf</code>
          </span>
          <button
            onClick={onClose}
            style={{ padding: "8px 18px", borderRadius: "9px", border: `1px solid ${t.border}`, background: "rgba(255,255,255,0.04)", color: t.textMuted, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Individual row in the list modal ─────────────────────────────────────────
const PayslipRow = ({ item, idx, total, copiedIdx, onView, onDownload, onCopy, t }) => {
  const [hov, setHov] = useState(false);
  const totalE = (item.emp?.earnings || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalD = (item.emp?.deductions || []).reduce((s, d) => s + (Number(d.amount) || 0), 0);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "grid", gridTemplateColumns: "28px 1fr 90px 90px 90px", gap: "0",
        padding: "11px 16px",
        background: hov ? `${t.primary}0F` : "transparent",
        borderBottom: idx < total - 1 ? `1px solid ${t.border}` : "none",
        transition: "background 0.12s", alignItems: "center",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: "600", color: t.textMuted }}>{idx + 1}</div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
          <span style={{ color: t.primary, display: "flex", flexShrink: 0 }}><FileIcon /></span>
          <span style={{ fontSize: "11px", fontWeight: "600", color: t.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.fileName}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingLeft: "22px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "10px", color: t.textMuted }}>
            <UserIcon />
            <span>{item.empName || "Unknown"}</span>
          </span>
          {item.empId && (
            <span style={{ fontSize: "9px", fontWeight: "600", color: t.primary, background: `${t.primary}1A`, padding: "1px 7px", borderRadius: "20px", border: `1px solid ${t.primary}33` }}>
              {item.empId}
            </span>
          )}
          {item.designation && (
            <span style={{ fontSize: "9px", color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px" }}>
              {item.designation}
            </span>
          )}
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "12px", fontWeight: "700", color: t.success }}>₹{fmt(item.netSalary)}</div>
        <div style={{ fontSize: "9px", color: t.textMuted, marginTop: "1px" }}>
          E:{fmt(totalE)} D:{fmt(totalD)}
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => onView(item)}
          title="Open in new tab"
          style={{ padding: "5px 10px", borderRadius: "7px", border: `1px solid ${t.primary}55`, background: `${t.primary}1A`, color: t.primary, fontSize: "10px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = `${t.primary}33`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${t.primary}1A`; }}
        >
          <EyeIcon /> View
        </button>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => onDownload(item)}
          title={`Download ${item.fileName}`}
          style={{ padding: "5px 10px", borderRadius: "7px", border: `1px solid ${t.successBrd}`, background: t.successBg, color: t.success, fontSize: "10px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = `rgba(34,197,94,0.22)`; }}
          onMouseLeave={e => { e.currentTarget.style.background = t.successBg; }}
        >
          <DownloadIcon /> Save
        </button>
      </div>
    </div>
  );
};

// ── Month Row Item ────────────────────────────────────────────────────────────
const MonthRowItem = ({ month, year, cacheEntry, onArrowClick, onViewList, t }) => {
  const [hov, setHov] = useState(false);
  const hasData = !!cacheEntry?.payslipList?.length;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 18px", width: "100%", boxSizing: "border-box",
        background: hov ? `${t.primary}1F` : hasData ? t.successBg : "rgba(255,255,255,0.03)",
        border: `1px solid ${hasData ? t.successBrd : hov ? `${t.primary}40` : t.border}`,
        borderRadius: "10px", transition: "background 0.15s, border-color 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "13px", flex: 1, minWidth: 0 }}>
        <div style={{ padding: "4px 10px", borderRadius: "7px", background: hasData ? t.successBg : `${t.primary}33`, border: `1px solid ${hasData ? t.successBrd : `${t.primary}66`}`, fontSize: "10px", fontWeight: "800", letterSpacing: "0.08em", color: hasData ? t.success : t.primary, minWidth: "38px", textAlign: "center", flexShrink: 0 }}>
          {month.slice(0, 3).toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: "14px", fontWeight: "500", color: t.textPrimary }}>{month} {year}</span>
          {hasData && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "2px" }}>
              <span style={{ fontSize: "10px", color: t.success, background: t.successBg, border: `1px solid ${t.successBrd}`, borderRadius: "20px", padding: "1px 8px", fontWeight: "600" }}>
                ✓ {cacheEntry.payslipList.length} payslips ready
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onViewList(); }}
                style={{ fontSize: "10px", color: t.primary, background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "600", textDecoration: "underline" }}
              >
                View list
              </button>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onArrowClick}
        style={{ width: "30px", height: "30px", borderRadius: "8px", border: `1px solid ${hasData ? t.successBrd : `${t.primary}66`}`, background: hasData ? t.successBg : `${t.primary}26`, color: hasData ? t.success : t.primary, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", flexShrink: 0 }}
        title={hasData ? "View payslip list" : "Generate payslips"}
        onMouseEnter={e => { e.currentTarget.style.background = hasData ? `${t.success}33` : `${t.primary}47`; e.currentTarget.style.transform = "translateX(2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = hasData ? t.successBg : `${t.primary}26`; e.currentTarget.style.transform = "translateX(0)"; }}
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const GeneratePayslip = () => {
  const themeStore = useSelector((s) => s.theme);
  const authStore = useSelector((s) => s.auth);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://seeknow.in/hrmsapi/api/";
  const token = authStore?.token || authStore?.accessToken || "";

  const reduxUser = authStore?.user || authStore?.data || {};
  const lsUser = (() => { try { return JSON.parse(localStorage.getItem("user_data") || "{}"); } catch { return {}; } })();
  const userData = Object.keys(reduxUser).length ? reduxUser : lsUser;

  const userId = userData?.user_id || userData?.userId || userData?.id || "1";
  const companyId = userData?.company_id || userData?.companyId || userData?.company || "";

  const uploadUserId = String(
    userData?.user_id || userData?.userId || userData?.id || userData?.admin_id || userData?.adminId || userData?.parent_id || userData?.owner_id || "2"
  ).trim();

  const isDark = themeStore?.theme === "dark" || !themeStore?.theme;
  const t = useMemo(() => buildTheme(isDark), [isDark]);

  const [selectedYear, setSelectedYear] = useState(CURR_YEAR);
  const [filterMonth, setFilterMonth] = useState("");

  const [linkCache, setLinkCache] = useState({});

  const [genModal, setGenModal] = useState(false);
  const [curStep, setCurStep] = useState(0);
  const [genDone, setGenDone] = useState(false);
  const [genError, setGenError] = useState("");
  const [genPayslipList, setGenPayslipList] = useState([]);
  const [totalEmp, setTotalEmp] = useState(0);
  const [doneEmp, setDoneEmp] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);

  const [listModal, setListModal] = useState(false);
  const [listModalData, setListModalData] = useState({ list: [], monthLabel: "" });

  const delay = (ms) => new Promise(r => setTimeout(r, ms));

  const visibleMonths = (() => {
    const all = getVisibleMonths(selectedYear);
    return filterMonth ? all.filter(m => m === filterMonth) : all;
  })();

  const authHeaders = () => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Content-Type": "application/json",
  });

  const openListModal = (month, year) => {
    const key = `${month}-${year}`;
    const cached = linkCache[key];
    if (cached?.payslipList?.length) {
      setListModalData({ list: cached.payslipList, monthLabel: `${month} ${year}` });
      setListModal(true);
    }
  };

  const handleArrowClick = (month, year) => {
    const key = `${month}-${year}`;
    if (linkCache[key]?.payslipList?.length) {
      openListModal(month, year);
      return;
    }
    runGenerate(month, year, key);
  };

  const runGenerate = async (month, year, cacheKey) => {
    setGenModal(true);
    setGenDone(false);
    setGenError("");
    setGenPayslipList([]);
    setCurStep(0);
    setTotalEmp(0);
    setDoneEmp(0);
    setSkippedCount(0);

    try {
      await delay(400);
      setCurStep(0);

      const monthNum = MONTH_NUM[month];
      let endpoint = calculateEarnedSalaryPath;
      if (!endpoint.startsWith("/")) endpoint = "/" + endpoint;
      const url = `${API_BASE_URL}${endpoint}`;

      const payload = {
        userId: String(userId),
        selectedDate: `${year}-${monthNum}-01`,
        branchId: companyId || "",
        staffId: "",
      };

      const response = await axios.post(url, payload, { headers: authHeaders(), timeout: 30000 });

      if (response.data?.status === "false") {
        throw new Error(response.data?.msg || "Failed to fetch salary data");
      }

      let apiData, rawList;
      if (response.data?.data) { apiData = response.data.data; rawList = apiData?.staffDetails || []; }
      else if (Array.isArray(response.data)) { rawList = response.data; apiData = { staffDetails: rawList }; }
      else { apiData = response.data; rawList = apiData?.staffDetails || []; }

      if (!rawList || rawList.length === 0) {
        throw new Error(`No salary data found for ${month} ${year}`);
      }

      const seen = new Set();
      const uniqueList = rawList.filter(r => {
        if (seen.has(r.staffId)) return false;
        seen.add(r.staffId);
        return true;
      });

      // ✅ Filter employees - only include those with valid CTC
      const validEmployees = [];
      const skippedEmployees = [];
      
      uniqueList.forEach(raw => {
        if (hasValidCTC(raw)) {
          validEmployees.push(raw);
        } else {
          skippedEmployees.push(raw.staffName || raw.employeeId || "Unknown");
        }
      });
      
      setSkippedCount(skippedEmployees.length);
      
      if (validEmployees.length === 0) {
        throw new Error(`No employees with valid CTC found for ${month} ${year}. ${skippedEmployees.length} employee(s) skipped due to missing CTC configuration.`);
      }

      const employees = validEmployees.map(mapApiEmployee).filter(Boolean);
      const companyName = apiData?.companyName || userData?.company_name || "Company";
      const generatedAt = new Date();

      setTotalEmp(employees.length);
      setCurStep(1);

      const payslipList = [];

      for (let i = 0; i < employees.length; i++) {
        const emp = employees[i];
        const fileName = buildFileName(uploadUserId, emp.employee_id || `EMP${i + 1}`, generatedAt, monthNum, year);

        const pdfInstance = pdf(
          <SalarySlipPDF
            emp={emp}
            monthLabel={`${month} ${year}`}
            companyName={companyName}
            monthNum={monthNum}
          />
        );

        const rawBlob = await pdfInstance.toBlob();
        console.log(`[${i + 1}/${employees.length}] ${emp.full_name} — blob size: ${rawBlob.size} bytes`);

        if (rawBlob.size === 0) {
          console.error("Empty PDF for", emp.full_name);
        }

        const blobUrl = URL.createObjectURL(rawBlob);

        payslipList.push({
          fileName,
          blobUrl,
          rawBlob,
          empId: emp.employee_id || "",
          empName: emp.full_name || "",
          designation: emp.designation || "",
          netSalary: Number(emp.netSalary) || 0,
          emp,
        });

        setDoneEmp(i + 1);
        await delay(20);
      }

      setCurStep(2);
      await delay(400);

      console.log("=== BEFORE UPLOAD ===");
      console.log("uploadUserId:", uploadUserId);
      console.log("Number of payslips:", payslipList.length);

      if (!payslipList || payslipList.length === 0) {
        setGenError("No payslips were generated.");
        return;
      }

      try {
        const fd = new FormData();
        fd.append("userId", String(uploadUserId || "2"));

        let addedCount = 0;

        payslipList.forEach((item, index) => {
          if (!item.rawBlob) {
            console.warn(`File ${index} skipped: No rawBlob`);
            return;
          }

          const file = new File([item.rawBlob], item.fileName, {
            type: "application/pdf",
            lastModified: Date.now(),
          });

          fd.append("payslipFiles[]", file);
          addedCount++;

          console.log(`✅ Added ${index + 1}: ${item.fileName} (${(file.size / 1024).toFixed(1)} KB)`);
        });

        if (addedCount === 0) {
          throw new Error("No valid PDF files to upload");
        }

        const uploadUrl = `${API_BASE_URL}/${uploadEmployeePayslipsPath.replace(/^\//, "")}`;
        console.log("Sending upload request to:", uploadUrl);

        const upRes = await axios.post(uploadUrl, fd, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 120000,
        });

        console.log("✅ Upload Response:", upRes.data);

      } catch (uploadErr) {
        console.error("❌ Upload Failed:", uploadErr);
        if (uploadErr.response) {
          console.error("Status:", uploadErr.response.status);
          console.error("Response Body:", uploadErr.response.data);
          setGenError(uploadErr.response.data?.message || "Upload failed");
        } else {
          setGenError(uploadErr.message || "Upload failed");
        }
      }

      setLinkCache(prev => ({
        ...prev,
        [cacheKey]: { payslipList, month, year },
      }));

      setGenPayslipList(payslipList);
      setGenDone(true);

    } catch (err) {
      let msg = "Something went wrong while generating payslips.";
      if (err.response) msg = err.response?.data?.message || err.response?.data?.msg || `Server error: ${err.response.status}`;
      else if (err.request) msg = "No response from server. Please check if the backend is running.";
      else msg = err.message;
      setGenError(msg);
      console.error("Generation error:", err);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/dashboard";
  };

  const selStyle = {
    appearance: "none", background: t.bgInput, border: `1px solid ${t.border}`,
    borderRadius: "9px", color: t.textPrimary, fontSize: "12px", fontWeight: "500",
    padding: "7px 32px 7px 12px", cursor: "pointer", outline: "none", minWidth: "120px",
  };

  return (
    <>
      <style>{`
        @keyframes spin  { to { transform:rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.5;} }
      `}</style>

      <GeneratingModal
        visible={genModal}
        currentStep={curStep}
        done={genDone}
        error={genError}
        payslipList={genPayslipList}
        onClose={() => setGenModal(false)}
        onViewList={() => {
          const lastKey = Object.keys(linkCache).pop();
          if (lastKey) {
            const [m, y] = lastKey.split("-");
            setListModalData({ list: linkCache[lastKey].payslipList, monthLabel: `${m} ${y}` });
            setListModal(true);
          }
        }}
        totalEmp={totalEmp}
        doneEmp={doneEmp}
        skippedCount={skippedCount}
        t={t}
      />

      <PayslipListModal
        visible={listModal}
        payslipList={listModalData.list}
        monthLabel={listModalData.monthLabel}
        onClose={() => setListModal(false)}
        t={t}
      />

      <div style={{ minHeight: "100vh", background: t.bgPage, fontFamily: "inherit", display: "flex", flexDirection: "column" }}>

        <div style={{ borderBottom: `1px solid ${t.border}`, background: t.bgPage, position: "sticky", top: 0, zIndex: 10, padding: "16px 32px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <Link to="/dashboard" style={{ textDecoration: "none" }}>
              <button
                onClick={handleBack}
                style={{ width: "34px", height: "34px", borderRadius: "9px", border: `1px solid ${t.border}`, background: "rgba(255,255,255,0.05)", color: t.textPrimary, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </Link>
            <div>
              <h1 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: t.textPrimary, letterSpacing: "-0.01em" }}>Bulk Payslip Generation</h1>
              <p style={{ margin: "2px 0 0", fontSize: "11px", color: t.textMuted }}>Select a month to generate individual payslips for ALL employees at once</p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingLeft: "46px" }}>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <select style={selStyle} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                <option value="">All Months</option>
                {getVisibleMonths(selectedYear).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <span style={{ position: "absolute", right: "9px", color: t.textMuted, pointerEvents: "none" }}><ChevronIcon /></span>
            </div>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <select style={selStyle} value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <span style={{ position: "absolute", right: "9px", color: t.textMuted, pointerEvents: "none" }}><ChevronIcon /></span>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: "24px 32px", overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <div style={{ width: "3px", height: "14px", background: t.gradient, borderRadius: "2px" }} />
            <span style={{ fontSize: "11px", fontWeight: "600", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {filterMonth || "All Months"} · {selectedYear}
            </span>
            <span style={{ fontSize: "11px", color: t.textMuted }}>
              ({visibleMonths.length} month{visibleMonths.length !== 1 ? "s" : ""})
            </span>
          </div>

          {visibleMonths.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "300px", gap: "12px" }}>
              <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: `${t.primary}1A`, border: `1px solid ${t.primary}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>📅</div>
              <p style={{ color: t.textMuted, fontSize: "13px", margin: 0 }}>No months available for {selectedYear}</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "8px" }}>
              {visibleMonths.map(month => {
                const key = `${month}-${selectedYear}`;
                return (
                  <MonthRowItem
                    key={key}
                    month={month}
                    year={selectedYear}
                    cacheEntry={linkCache[key] || null}
                    onArrowClick={() => handleArrowClick(month, selectedYear)}
                    onViewList={() => openListModal(month, selectedYear)}
                    t={t}
                  />
                );
              })}
            </div>
          )}

          <div style={{ marginTop: "24px", padding: "12px 16px", background: `${t.primary}10`, borderRadius: "8px", border: `1px solid ${t.primary}30`, fontSize: "11px", color: t.textMuted, textAlign: "center" }}>
            💡 <strong>Individual Payslips</strong> — Each employee gets their own PDF file named{" "}
            <code style={{ background: `${t.primary}1A`, padding: "1px 5px", borderRadius: "4px", color: t.primary }}>userId_employeeId_datetime.pdf</code>.
            Click any month row to generate or view existing payslips.
            <br />
            <span style={{ color: t.warning, display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "6px" }}>
              <WarningIcon /> Employees without valid CTC configuration will be automatically skipped.
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneratePayslip;